import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { PortalResourceInviteMembers } from 'src/resource/PortalResource/types';
import { ActionPromiseButton } from 'src/web/components/Buttons/ActionButton/ActionPromiseButton';
import { Button } from 'src/web/components/Buttons/Button/Button';
import { IconButton } from 'src/web/components/Buttons/IconButton/IconButton';
import { InputLabel } from 'src/web/components/InputLabel/component';
import { pNotifyAlert } from 'src/web/components/Pnotify/component';
import { OL_COLOUR_NAVY_BABY } from 'src/web/components/Style/colours';
import { Tooltip } from 'src/web/Tooltip/Tooltip';
import { Link } from 'src/web/containers/Link';
import { ROUTE_USER_PROFILE_PAGE } from 'src/ReduxStorage/routeActions';
import { AccountCreationOption } from '../AccountCreationOption/component';
import { EMAIL_VALID_REGEX, successAddTeamMembersSettings } from '../consts';
import { useInviteMembersApi } from '../hooks';
import {
  StyledInstitutionTable,
  EmailOptionWrapper,
  StyledButton,
  StyledEmailTextField,
  RoleSelect,
  ErrorText,
  ButtonActionWrapper,
  StyledTooltipOverlay,
  StyledTextField,
} from '../styles';
import { useSuggestUserApi } from '../../ViewTeamMembers/hooks';
import { NameEmailInfo, UserEmail } from '../../styles';

type UsersDataType = {
  email: string;
  externalId: string;
  fullName: string;
  role: 'staff' | 'admin';
  groups: '';
};

const EmailField: React.FC<{ email: string }> = ({ email }) => {
  const [queryResult] = useSuggestUserApi(email, '');
  if (queryResult && queryResult.length === 1) {
    const { fullName, profileName, smallAvatar } = queryResult[0];
    return (
      <td>
        <NameEmailInfo>
          <img alt="user-avatar" src={smallAvatar} />
          <div>
            <Link
              to={{
                type: ROUTE_USER_PROFILE_PAGE,
                params: {
                  profileName,
                },
              }}
              target="_blank"
            >
              {fullName}
            </Link>
            <UserEmail>{email}</UserEmail>
          </div>
        </NameEmailInfo>
      </td>
    );
  }
  return (
    <>
      <td>{email}</td>
    </>
  );
};

export interface AddTeamMembersByCsvProps {
  institutionPath: string;
  onCancelClicked: () => void;
  onEmailListModeChosen: () => void;
}

const ROLE_OPTIONS = [
  { label: 'Course creator', value: 'staff' },
  { label: 'Institution administrator', value: 'admin' },
];

export const AddTeamMembersByEmail: FunctionComponent<AddTeamMembersByCsvProps> = ({
  institutionPath,
  onEmailListModeChosen,
  onCancelClicked,
}) => {
  const [emailInputs, setEmailInputs] = useState('');
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [usersData, setUsersData] = useState<UsersDataType[]>([]);
  const [accountCreationState, setAccountCreationState] = useState({
    createAccount: false,
    skipInvitationEmail: false,
  });

  const { error: errorInviteMembers, inviteMembers } = useInviteMembersApi(
    institutionPath
  );

  const updateUserData = (
    index: number,
    field: keyof UsersDataType,
    newValue: UsersDataType[typeof field]
  ): void => {
    const copiedUsersData = [...usersData];
    copiedUsersData[index] = {
      ...copiedUsersData[index],
      [field]: newValue,
    };
    setUsersData(copiedUsersData);
  };

  const removeUserData = (deletedIndex: number): void => {
    // Show warning to the users to fill out all the fullnames if the user decide to create the account
    setUsersData((prev) =>
      prev.filter((_, currIndex) => currIndex !== deletedIndex)
    );
  };

  const isShowingCreateAccountWarn = useMemo(() => {
    if (accountCreationState.createAccount) {
      return usersData.some(({ fullName }) => !fullName);
    }
    return false;
  }, [usersData, accountCreationState.createAccount]);

  const onAddTeamMembers = useCallback(() => {
    const payload: PortalResourceInviteMembers['args'] = {
      invites: usersData.map((user) => ({ ...user })),
      generateAccount: accountCreationState.createAccount,
      sendEmail: !accountCreationState.skipInvitationEmail,
    };
    return inviteMembers(payload);
  }, [usersData, accountCreationState, inviteMembers]);

  const onAddToListClicked = useCallback(() => {
    onEmailListModeChosen();
    const parsedEmails = emailInputs.split(',').map((email) => email.trim());
    const checkIsEmailValid = parsedEmails.every((email) =>
      EMAIL_VALID_REGEX.test(email)
    );
    if (checkIsEmailValid) {
      setIsEmailInvalid(false);
      parsedEmails.forEach((email) => {
        setUsersData((prev) => [
          ...prev,
          {
            email,
            externalId: '',
            fullName: '',
            role: 'staff',
            groups: '',
          },
        ]);
      });
      setEmailInputs('');
    } else {
      setIsEmailInvalid(true);
    }
  }, [
    onEmailListModeChosen,
    emailInputs,
    setIsEmailInvalid,
    setUsersData,
    EMAIL_VALID_REGEX,
  ]);

  const onKeyDownEmailTextField = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (event.keyCode === 13) {
      // handle on Enter pressed
      onAddToListClicked();
    }
  };

  return (
    <>
      <EmailOptionWrapper>
        <StyledEmailTextField
          name="email"
          onKeyDown={onKeyDownEmailTextField}
          error={isEmailInvalid ? 'Email is not in the correct format' : ''}
          label="Add team members by email"
          value={emailInputs}
          placeholder="example1@organization.com, example2@organization.com, example3@organization.com"
          onValueChange={(value) => {
            setEmailInputs(value);
          }}
        />
        <StyledButton label="Add to list" onClick={onAddToListClicked} />
      </EmailOptionWrapper>
      {usersData.length > 0 && (
        <>
          <InputLabel>Review and fill in additional fields</InputLabel>
          <StyledInstitutionTable>
            <thead>
              <tr>
                <th>Email</th>
                <th>
                  Institution role{' '}
                  <Tooltip
                    placement="bottom"
                    trigger="click"
                    overlay={
                      <StyledTooltipOverlay>
                        Admins can access all institution settings. Course
                        creators cannot access institution settings, but can
                        create courses within the institution.
                      </StyledTooltipOverlay>
                    }
                  >
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      color={OL_COLOUR_NAVY_BABY}
                    />
                  </Tooltip>
                </th>
                <th>Full name</th>
                <th>Institution ID </th>
                <th>Groups</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {usersData.map(
                ({ email, externalId, fullName, groups, role }, index) => (
                  <tr key={`${email}`}>
                    <EmailField email={email} />
                    <td>
                      <RoleSelect
                        options={ROLE_OPTIONS}
                        value={ROLE_OPTIONS.find((opt) => opt.value === role)}
                        onValueChange={(newValue) => {
                          if (newValue) {
                            updateUserData(index, 'role', newValue.value);
                          }
                        }}
                      />
                    </td>
                    <td>
                      <StyledTextField
                        value={fullName}
                        onValueChange={(newValue) =>
                          updateUserData(index, 'fullName', newValue)
                        }
                      />
                    </td>
                    <td>
                      <StyledTextField
                        value={externalId}
                        onValueChange={(newValue) =>
                          updateUserData(index, 'externalId', newValue)
                        }
                      />
                    </td>
                    <td>
                      <StyledTextField
                        value={groups}
                        placeholder="group A, groupB, groupC"
                        onValueChange={(newValue) =>
                          updateUserData(index, 'groups', newValue)
                        }
                      />
                    </td>
                    <td>
                      <IconButton
                        hiddenLabel="Delete me"
                        icon="times"
                        onClick={() => removeUserData(index)}
                      />
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </StyledInstitutionTable>
          <AccountCreationOption
            createAccountValue={accountCreationState.createAccount}
            createAccountError={
              isShowingCreateAccountWarn
                ? 'To automatically create accounts, you must provide the full names of each team member.'
                : ''
            }
            skipInvitationValue={accountCreationState.skipInvitationEmail}
            onCreateAccountValueChange={() =>
              setAccountCreationState((prev) => ({
                ...prev,
                createAccount: !prev.createAccount,
              }))
            }
            onSkipInvitationValueChange={() =>
              setAccountCreationState((prev) => ({
                ...prev,
                skipInvitationEmail: !prev.skipInvitationEmail,
              }))
            }
          />
          <ButtonActionWrapper>
            <Button
              onClick={() => {
                onCancelClicked();
                // Reset data
                setUsersData([]);
              }}
            >
              Cancel
            </Button>
            <div>
              <ErrorText>{errorInviteMembers}</ErrorText>
              <ActionPromiseButton
                colour="kelp"
                onClick={onAddTeamMembers}
                onSuccess={() =>
                  pNotifyAlert(successAddTeamMembersSettings(usersData.length))
                }
                label="Add team members"
              />
            </div>
          </ButtonActionWrapper>
        </>
      )}
    </>
  );
};
